import { useMemo } from "react";
import { parseTotp, totpCodeAt } from "@passwordflow/crypto";
import { useSecretCopy } from "../../app/clipboard.js";
import { markUsed } from "../../app/recentUse.js";
import type { Entry } from "../../lib/entries.js";
import { safeHttpUrl } from "../../lib/url.js";
import { useToast } from "../../ui/Toast.js";
import { fieldLabels } from "../../vaultPayload.js";

export interface EntryActions {
  readonly copyPassword: (entry: Entry) => void;
  readonly copyUsername: (entry: Entry) => void;
  /** Copies the current 2FA code of a login with a stored TOTP secret. */
  readonly copyTotp: (entry: Entry) => void;
  /** Opens the entry's http(s) address in a new tab; other schemes are refused. */
  readonly openUrl: (entry: Entry) => void;
}

/** Quick actions shared by list rows, dashboard tiles and the command palette. Each marks the entry as recently used. */
export function useEntryActions(): EntryActions {
  const copy = useSecretCopy();
  const toast = useToast();

  return useMemo(
    (): EntryActions => ({
      copyPassword: (entry: Entry): void => {
        const label = fieldLabels(entry.data.type).password;
        if (entry.data.password === "") {
          toast.notify(`Für „${entry.data.title}“ ist kein Wert für „${label}“ hinterlegt.`);
          return;
        }
        void copy(entry.data.password, `${label} kopiert`).then((ok: boolean): void => {
          if (ok) {
            markUsed(entry.meta.id);
          }
        });
      },
      copyUsername: (entry: Entry): void => {
        const label = fieldLabels(entry.data.type).username;
        if (entry.data.username === "") {
          toast.notify(`Für „${entry.data.title}“ ist kein Wert für „${label}“ hinterlegt.`);
          return;
        }
        void copy(entry.data.username, `${label} kopiert`).then((ok: boolean): void => {
          if (ok) {
            markUsed(entry.meta.id);
          }
        });
      },
      copyTotp: (entry: Entry): void => {
        let code: string;
        try {
          code = totpCodeAt(parseTotp(entry.data.totp), Date.now());
        } catch {
          toast.notify(`Für „${entry.data.title}“ ist kein gültiger 2FA-Schlüssel hinterlegt.`);
          return;
        }
        void copy(code, "2FA-Code kopiert").then((ok: boolean): void => {
          if (ok) {
            markUsed(entry.meta.id);
          }
        });
      },
      openUrl: (entry: Entry): void => {
        const link = safeHttpUrl(entry.data.url);
        if (link === undefined) {
          toast.notify(`Für „${entry.data.title}“ ist keine gültige Webadresse hinterlegt.`);
          return;
        }
        markUsed(entry.meta.id);
        window.open(link, "_blank", "noopener,noreferrer");
      }
    }),
    [copy, toast]
  );
}
